import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupIcon from '@mui/icons-material/Group';
import EventIcon from '@mui/icons-material/Event';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import useAuth from '../hooks/useAuth';
import {
  listCohorts, createCohort, updateCohort, endCohort, regenerateEnrollmentLink,
} from '../services/collegeApi';
import { formatDate, copyToClipboard } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';

const ENROLLMENT_BASE = 'https://cwc.link/';

export default function CohortManagementPage() {
  const { user } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | cohort obj
  const [endingCohort, setEndingCohort] = useState(null);
  const [snack, setSnack] = useState('');

  const refresh = () => {
    if (!user?.college_id) return;
    setLoading(true);
    listCohorts(user.college_id)
      .then((d) => setCohorts(d?.cohorts || []))
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [user]);

  const handleSave = async (formData) => {
    try {
      if (editing === 'new') {
        await createCohort(user.college_id, formData);
        setSnack('Cohort created');
      } else {
        await updateCohort(editing.cohort_id, formData);
        setSnack('Cohort updated');
      }
      setEditing(null);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  const handleEnd = async () => {
    try {
      await endCohort(endingCohort.cohort_id);
      setSnack('Cohort ended');
      setEndingCohort(null);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
      setEndingCohort(null);
    }
  };

  const handleCopyLink = async (cohort) => {
    const url = ENROLLMENT_BASE + (cohort.enrollment_link_code || '');
    await copyToClipboard(url);
    setSnack('Enrollment link copied');
  };

  const handleRegenerate = async (cohort) => {
    try {
      await regenerateEnrollmentLink(cohort.cohort_id);
      setSnack('Enrollment link regenerated');
      refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Cohorts</Typography>
          <Typography variant="body1" color="text.secondary">
            Semester-based groups with credit requirements and shareable enrollment links.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          New Cohort
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : cohorts.length === 0 ? (
        <Paper>
          <EmptyState
            icon={<GroupIcon fontSize="inherit" />}
            title="No cohorts yet"
            message="Create your first cohort to start enrolling students."
            action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>Create Cohort</Button>}
          />
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {cohorts.map((c) => (
            <Grid item xs={12} md={6} lg={4} key={c.cohort_id}>
              <CohortCard
                cohort={c}
                onEdit={() => setEditing(c)}
                onCopyLink={() => handleCopyLink(c)}
                onRegenerate={() => handleRegenerate(c)}
                onEnd={() => setEndingCohort(c)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {editing && (
        <CohortDialog
          open={!!editing}
          cohort={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      <ConfirmDialog
        open={!!endingCohort}
        title="End cohort?"
        message={`This will mark ${endingCohort?.name || 'this cohort'} as completed and freeze all enrolled student credit states. This cannot be undone.`}
        confirmText="End Cohort"
        confirmColor="error"
        onConfirm={handleEnd}
        onCancel={() => setEndingCohort(null)}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function CohortCard({ cohort, onEdit, onCopyLink, onRegenerate, onEnd }) {
  const enrolledCount = cohort.enrolled_count ?? cohort.student_count ?? 0;
  const reqs = cohort.credit_requirements || {};
  const link = cohort.enrollment_link_code ? ENROLLMENT_BASE + cohort.enrollment_link_code : null;

  return (
    <Paper sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap title={cohort.name}>
            {cohort.name}
          </Typography>
          {cohort.course_code && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {cohort.course_code}
            </Typography>
          )}
        </Box>
        <StatusChip status={cohort.status} kind="cohort" />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        <EventIcon fontSize="small" />
        <Typography variant="body2">
          {formatDate(cohort.start_date)} → {formatDate(cohort.end_date)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        <GroupIcon fontSize="small" />
        <Typography variant="body2">
          {enrolledCount} enrolled
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Credit requirements
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <ReqChip label="Hours" value={reqs.hours} />
          <ReqChip label="Assessments" value={reqs.assessments} />
        </Stack>
      </Box>

      {link && (
        <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 1, p: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Enrollment link</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={link}>
              {link}
            </Typography>
            <Tooltip title="Copy link">
              <IconButton size="small" onClick={onCopyLink}><ContentCopyIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Regenerate code">
              <IconButton size="small" onClick={onRegenerate}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
        {cohort.status !== 'COMPLETED' && (
          <Button size="small" startIcon={<StopCircleIcon />} color="error" onClick={onEnd}>
            End
          </Button>
        )}
        <Button size="small" startIcon={<EditIcon />} variant="outlined" onClick={onEdit}>
          Edit
        </Button>
      </Stack>
    </Paper>
  );
}

function ReqChip({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function CohortDialog({ open, cohort, onClose, onSave }) {
  const isNew = !cohort;
  const [name, setName] = useState(cohort?.name || '');
  const [startDate, setStartDate] = useState(cohort?.start_date || '');
  const [endDate, setEndDate] = useState(cohort?.end_date || '');
  const [courseCode, setCourseCode] = useState(cohort?.course_code || '');
  const [hours, setHours] = useState(cohort?.credit_requirements?.hours ?? 60);
  const [assessments, setAssessments] = useState(cohort?.credit_requirements?.assessments ?? 6);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSave({
        name,
        start_date: startDate,
        end_date: endDate,
        course_code: courseCode || null,
        credit_requirements: {
          hours: Number(hours),
          assessments: Number(assessments),
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isNew ? 'New Cohort' : `Edit ${cohort.name}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Cohort name"
              placeholder="Fall 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
            </Stack>
            <TextField
              label="Course code (optional)"
              placeholder="CYB-480"
              value={courseCode}
              onChange={(e) => setCourseCode(e.target.value)}
              fullWidth
              helperText="Used for transcripts and accreditor reports"
            />
            <Divider />
            <Typography variant="subtitle2">Credit requirements</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Each assessment includes a mandatory mentor review.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Hours"
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                inputProps={{ min: 0 }}
                required
                fullWidth
              />
              <TextField
                label="Assessments"
                type="number"
                value={assessments}
                onChange={(e) => setAssessments(e.target.value)}
                inputProps={{ min: 0 }}
                required
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : (isNew ? 'Create Cohort' : 'Save Changes')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
